import { Button, TextField, Typography } from '@braskai/ui-kit';

import { Stack } from '@mui/material';

import { OTHER_TEXT_MAX_LENGTH, RECORDING_SURVEY_COPY, RECORDING_SURVEY_TEST_IDS } from '../../lib/constants';
import { OptionItem } from '../OptionItem';

type Option<TId extends string> = { id: TId; label: string };

type Props<TId extends string> = {
  title: string;
  canGoBack: boolean;
  isContinueDisabled: boolean;
  isOtherSelected: boolean;
  isRoleQuestion: boolean;
  options: readonly Option<TId>[];
  otherText: string;
  selected: TId | undefined;
  subtitle: string;
  onBack: () => void;
  onContinue: () => void;
  onOtherTextChange: (value: string) => void;
  onSelect: (id: TId) => void;
};

export const QuestionStep = <TId extends string>({
  title,
  canGoBack,
  isContinueDisabled,
  isOtherSelected,
  isRoleQuestion,
  options,
  otherText,
  selected,
  subtitle,
  onBack,
  onContinue,
  onOtherTextChange,
  onSelect,
}: Props<TId>) => (
  <Stack gap="16px" sx={{ width: '100%' }}>
    <Stack gap="4px" sx={{ alignItems: 'center', textAlign: 'center' }}>
      <Typography color="grey.900" variant="title-4-md">
        {title}
      </Typography>
      <Typography color="grey.500" variant="body-2">
        {subtitle}
      </Typography>
    </Stack>

    <Stack
      aria-label={title}
      gap="8px"
      role="radiogroup"
      sx={isRoleQuestion ? { '& > button:nth-of-type(3)': { marginTop: '4px' } } : undefined}
    >
      {options.map((option) => (
        <OptionItem
          key={option.id}
          label={option.label}
          dataTestId={RECORDING_SURVEY_TEST_IDS.option(option.id)}
          isSelected={selected === option.id}
          onSelect={() => onSelect(option.id)}
        />
      ))}
    </Stack>

    {isOtherSelected && (
      <TextField
        slotProps={{
          htmlInput: { 'data-testid': RECORDING_SURVEY_TEST_IDS.otherTextInput, maxLength: OTHER_TEXT_MAX_LENGTH },
        }}
        sx={{
          '& .MuiInputBase-root': {
            alignItems: 'flex-start',
            height: 80,
            minHeight: 80,
          },
          marginTop: '-4px',
        }}
        placeholder={RECORDING_SURVEY_COPY.otherPlaceholder}
        value={otherText}
        variant="outlined"
        fullWidth
        multiline
        onChange={(event) => onOtherTextChange(event.target.value)}
      />
    )}

    <Stack gap="8px">
      <Button
        data-testid={RECORDING_SURVEY_TEST_IDS.continue}
        disabled={isContinueDisabled}
        size="lg"
        variant="primary"
        fullWidth
        onClick={onContinue}
      >
        {RECORDING_SURVEY_COPY.continue}
      </Button>
      {canGoBack && (
        <Button data-testid="question-step-back" size="lg" variant="outlined" fullWidth onClick={onBack}>
          {RECORDING_SURVEY_COPY.back}
        </Button>
      )}
    </Stack>
  </Stack>
);
